/*
 * @Copyright 2024. Institute for Future Intelligence, Inc.
 */

import React, { useCallback, useEffect, useLayoutEffect, useRef } from 'react';
import { throttle } from 'lodash';

interface SplitPaneProps {
  hideGallery: boolean;
  children: [React.ReactNode, React.ReactNode];
  defaultSize?: number; // left side: 0 - 100 %
  minWidth?: number; // left side: 0 - 100 %
  maxWidth?: number; // left side: 0 - 100 %
  wait?: number; // throttle wait time
  onChange?: (size: number) => void;
  // Identity of the currently-open project. Changing it re-applies the saved
  // splitter position even when defaultSize happens to be numerically equal to
  // the previously-open project's — otherwise reopening project B with the same
  // split as project A would skip the layout effect and leave the sash stale.
  restoreKey?: string | number;
}

const SplitPane = React.memo(
  ({
    hideGallery,
    defaultSize = 50,
    minWidth = 25,
    maxWidth = 75,
    children,
    wait = 50,
    onChange,
    restoreKey,
  }: SplitPaneProps) => {
    const [leftChild, rightChild] = children;
    const pointerDownRef = useRef(false);

    const updateCSSPercentWidth = (pos: number) => {
      const sp = document.querySelector('.split-pane') as any;
      if (sp) {
        sp.style.setProperty('--percentWidth', pos + '%');
      }
    };

    const handlePointerDown = () => {
      pointerDownRef.current = true;
    };

    const handlePointerMove = useCallback(
      throttle((e: React.PointerEvent<HTMLDivElement>) => {
        if (!pointerDownRef.current) return;

        const percentWidth = Math.max(minWidth, Math.min(maxWidth, (e.clientX / window.innerWidth) * 100));
        updateCSSPercentWidth(percentWidth);

        if (onChange) {
          onChange(percentWidth);
        }
      }, wait),
      [],
    );

    useEffect(() => {
      const handlePointerUp = () => {
        pointerDownRef.current = false;
      };
      window.addEventListener('pointerup', handlePointerUp);
      return () => window.removeEventListener('pointerup', handlePointerUp);
    }, []);

    // Sync the CSS split position to the controlled `defaultSize` whenever it
    // changes — e.g. when a project loads and restores its saved
    // chamberViewerPercentWidth. useLayoutEffect so the position is applied
    // before paint (no flash of the previous split). When the gallery is shown
    // (hideGallery prop true) the sash is active and the saved size applies;
    // otherwise the gallery is collapsed to 0.
    useLayoutEffect(() => {
      updateCSSPercentWidth(hideGallery ? defaultSize : 0);
    }, [hideGallery, defaultSize, restoreKey]);

    return (
      <div className="split-pane" onPointerMove={handlePointerMove}>
        <div className="left-child">{leftChild}</div>
        {hideGallery && <div className="sash" onPointerDown={handlePointerDown} />}
        <div className="right-child" id="split-pane-right-child">
          {rightChild}
        </div>
      </div>
    );
  },
  (prev, next) => {
    // Must include defaultSize: when a project loads and restores its saved
    // splitter position, defaultSize changes — if we only compared hideGallery
    // and onChange (which can be referentially stable), React would skip the
    // re-render and the layout effect that moves the splitter would never run.
    return (
      prev.hideGallery === next.hideGallery &&
      prev.onChange === next.onChange &&
      prev.defaultSize === next.defaultSize &&
      prev.minWidth === next.minWidth &&
      prev.maxWidth === next.maxWidth &&
      prev.restoreKey === next.restoreKey
    );
  },
);

export default SplitPane;
